"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { getDiscoveryProperties, type DiscoveryFamily } from "@/lib/analytics/discovery-link-events";

interface Props {
	containerId: string;
	sourceFamily: DiscoveryFamily;
	sourceSlug: string;
	locale: string;
	channel: string;
}

/** Observe server-rendered links, including late streamed resources. Never rewrite hrefs. */
export function DiscoveryLinksTracker({ containerId, sourceFamily, sourceSlug, locale, channel }: Props) {
	useEffect(() => {
		const container = document.getElementById(containerId);
		if (!container) return;
		const seen = new Set<string>();
		const anchors = new Set<HTMLAnchorElement>();
		const visible = new Set<HTMLAnchorElement>();
		const timers = new Map<HTMLAnchorElement, ReturnType<typeof setTimeout>>();
		const canCapture = () => {
			try {
				return posthog.has_opted_out_capturing() === false;
			} catch {
				// If the SDK cannot report its opt-out state, fail closed for this event.
				return false;
			}
		};
		const properties = (anchor: HTMLAnchorElement) =>
			getDiscoveryProperties({
				sourceFamily,
				sourceSlug,
				href: anchor.href,
				origin: window.location.origin,
				locale,
				channel,
				placement: anchor.closest<HTMLElement>("[data-discovery-placement]")?.dataset.discoveryPlacement,
			});
		const capture = (event: string, value: NonNullable<ReturnType<typeof properties>>) => {
			if (!canCapture()) return false;
			try {
				posthog.capture(event, value);
			} catch {
				/* Measurement must never block a link. */
				return false;
			}
			return true;
		};
		const cancel = (anchor: HTMLAnchorElement) => {
			const timer = timers.get(anchor);
			if (timer !== undefined) clearTimeout(timer);
			timers.delete(anchor);
		};
		const update = (anchor: HTMLAnchorElement) => {
			const value = properties(anchor);
			if (
				!container.contains(anchor) ||
				!visible.has(anchor) ||
				document.visibilityState !== "visible" ||
				!value ||
				!canCapture() ||
				seen.has(value.link_id)
			) {
				cancel(anchor);
				return;
			}
			if (timers.has(anchor)) return;
			timers.set(
				anchor,
				setTimeout(() => {
					timers.delete(anchor);
					if (!canCapture()) return;
					const latest = properties(anchor);
					if (
						!container.contains(anchor) ||
						!visible.has(anchor) ||
						document.visibilityState !== "visible" ||
						!latest ||
						seen.has(latest.link_id)
					)
						return;
					if (capture("discovery_link_viewed", latest)) seen.add(latest.link_id);
				}, 500),
			);
		};
		const observer =
			typeof IntersectionObserver === "undefined"
				? null
				: new IntersectionObserver(
						(entries) => {
							for (const entry of entries) {
								const anchor = entry.target as HTMLAnchorElement;
								if (entry.isIntersecting && entry.intersectionRatio >= 0.5) visible.add(anchor);
								else visible.delete(anchor);
								update(anchor);
							}
						},
						{ threshold: [0, 0.5] },
					);
		const discover = () => {
			for (const anchor of anchors) {
				if (!container.contains(anchor)) {
					cancel(anchor);
					observer?.unobserve(anchor);
					anchors.delete(anchor);
					visible.delete(anchor);
				}
			}
			for (const anchor of container.querySelectorAll<HTMLAnchorElement>("a[href]")) {
				if (!anchors.has(anchor) && properties(anchor)) {
					anchors.add(anchor);
					observer?.observe(anchor);
				}
			}
		};
		const handleClick = (event: MouseEvent) => {
			const anchor =
				event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
			if (!anchor || !container.contains(anchor)) return;
			const value = properties(anchor);
			if (value) capture("discovery_link_clicked", value);
		};
		const handleVisibility = () => {
			for (const anchor of anchors) update(anchor);
		};
		const mutations = new MutationObserver(discover);
		discover();
		mutations.observe(container, { childList: true, subtree: true });
		container.addEventListener("click", handleClick);
		document.addEventListener("visibilitychange", handleVisibility);
		return () => {
			observer?.disconnect();
			mutations.disconnect();
			for (const anchor of timers.keys()) cancel(anchor);
			container.removeEventListener("click", handleClick);
			document.removeEventListener("visibilitychange", handleVisibility);
		};
	}, [channel, containerId, locale, sourceFamily, sourceSlug]);
	return null;
}
